import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "zustand";
import type { PushPermission } from "../adapters";
import { useStaffCore } from "../provider";
import { DEVICE_ID_KEY, deviceIdOf, pushStatus, shouldOfferPush } from "../push/offer";
import { useCan } from "./use-navigation";
import { useRestaurantId } from "./use-order";

/**
 * Push notifications for this device (P3-T32; realtime-notifications.md,
 * Push rules). With permission granted, the device is registered for the
 * active restaurant (again after a switch, which also renews the
 * subscription). `offer` says when to show "Turn on notifications"
 * (push/offer.ts); `enable()` must run from a click, as browsers ask only
 * then; `dismiss()` is "Not now".
 */
export function usePush() {
	const core = useStaffCore();
	const adapter = core.platform.push;
	const trpc = useTRPC();
	const restaurantId = useRestaurantId();
	const support = useStore(core.support, (s) => s.session !== null);
	const prompt = useStore(core.pushPrompt);
	const canDecide = useCan("batch.decide");
	const canAnswer = useCan("order.edit");
	const canAct = canDecide || canAnswer;
	const config = useQuery({
		...trpc.staff.devices.getPushConfig.queryOptions(),
		enabled: Boolean(adapter && restaurantId),
		staleTime: Number.POSITIVE_INFINITY,
	});
	const key = config.data?.webPushPublicKey ?? null;
	const [permission, setPermission] = useState<PushPermission>(
		() => adapter?.permission() ?? "unsupported",
	);
	const [working, setWorking] = useState(false);
	const [failed, setFailed] = useState(false);
	const [registered, setRegistered] = useState(false);
	/** Turn on after a failure runs the registration again. */
	const [attempt, setAttempt] = useState(0);
	const queryClient = useQueryClient();
	const register = useMutation(
		trpc.staff.devices.register.mutationOptions({
			// More > This device shows the on-duty switch and preferences once the device is known.
			onSuccess: (device) =>
				queryClient.setQueryData(trpc.staff.devices.get.queryKey({ id: device.id }), device),
		}),
	);
	const registerAsync = register.mutateAsync;

	// Granted: keep this device registered for the restaurant in use.
	useEffect(() => {
		if (!adapter || !key || !restaurantId || support || permission !== "granted") return;
		let cancelled = false;
		void attempt;
		void (async () => {
			try {
				const subscription = (await adapter.current()) ?? (await adapter.subscribe(key));
				if (!subscription || cancelled) return;
				await registerAsync({ id: await deviceIdOf(core.platform.storage), webPush: subscription });
				if (!cancelled) {
					setRegistered(true);
					setFailed(false);
				}
			} catch {
				if (!cancelled) setFailed(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [adapter, key, restaurantId, support, permission, registerAsync, core, attempt]);

	const enable = useCallback(async () => {
		if (!adapter || !key) return false;
		setWorking(true);
		setFailed(false);
		try {
			const subscription = await adapter.subscribe(key);
			setPermission(adapter.permission());
			setAttempt((n) => n + 1);
			return subscription !== null;
		} catch {
			setFailed(true);
			return false;
		} finally {
			setWorking(false);
		}
	}, [adapter, key]);

	return {
		status: pushStatus(adapter ? permission : "unsupported", key !== null, support, registered),
		offer: shouldOfferPush({
			permission: adapter ? permission : "unsupported",
			configured: key !== null,
			support,
			canAct,
			engaged: prompt.engaged,
			dismissedAt: prompt.dismissedAt,
			now: Date.now(),
		}),
		working,
		failed,
		enable,
		dismiss: useCallback(() => core.pushPrompt.getState().dismiss(Date.now()), [core]),
	};
}

/**
 * Signs out, first telling the server this device gets no more pushes (at
 * most 2 seconds, so a dead connection never keeps anyone signed in).
 */
export function useSignOut() {
	const core = useStaffCore();
	return useCallback(async () => {
		const forget = async () => {
			const id = await core.platform.storage.getItem(DEVICE_ID_KEY).catch(() => null);
			if (!id) return;
			await Promise.all([
				core.client.staff.devices.unregister.mutate({ id }).catch(() => {}),
				core.platform.push?.unsubscribe().catch(() => {}),
			]);
		};
		try {
			await Promise.race([forget(), new Promise((resolve) => setTimeout(resolve, 2000))]);
		} finally {
			// A quick switch ends with the session (P6-T11).
			core.staffSwitch.getState().set(null);
			await core.platform.auth.signOut();
		}
	}, [core]);
}
